import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logFirebaseEvent } from '@utils/firebaseAnalytics';

/** GA4 measurement ID already loaded in `web/index.html`. */
export const GA4_MEASUREMENT_ID = 'G-EWWD4XJVCX';

export const GA_EVENT_SIGNUP_COMPLETE = 'signup_complete';
/** First successful onboarding finish — map as a GA4 conversion in Admin. */
export const GA_EVENT_ACTIVATION = 'activation';

const SIGNUP_DEDUP_KEY = 'ga:signup_complete_sent';
const ACTIVATION_DEDUP_KEY = 'ga:activation_sent';

type GaParamValue = string | number | boolean;
type GaParams = Record<string, GaParamValue>;

type GtagFn = (...args: unknown[]) => void;

function getGtag(): GtagFn | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const gtag = (window as Window & { gtag?: GtagFn }).gtag;
  return typeof gtag === 'function' ? gtag : null;
}

function queueOrFireGtag(eventName: string, params?: GaParams): void {
  const gtag = getGtag();
  if (gtag) {
    gtag('event', eventName, params);
    return;
  }
  // gtag loads after `window.load` — retry briefly so signup right after load still lands.
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    const ready = getGtag();
    if (ready) {
      clearInterval(timer);
      ready('event', eventName, params);
      return;
    }
    if (attempts >= 20) clearInterval(timer);
  }, 250);
}

function logMetaCompleteRegistration(method?: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const FB = (window as Window & { FB?: { AppEvents?: { logEvent: (n: string, v?: number, p?: object) => void } } }).FB;
    FB?.AppEvents?.logEvent?.('fb_mobile_complete_registration', undefined, {
      fb_registration_method: method || 'unknown',
    });
  } catch {
    /* ignore */
  }
}

async function alreadySent(key: string): Promise<boolean> {
  try {
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(key) === '1';
    }
    return (await AsyncStorage.getItem(key)) === '1';
  } catch {
    return false;
  }
}

async function markSent(key: string): Promise<void> {
  try {
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, '1');
      return;
    }
    await AsyncStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Fire a conversion-oriented analytics event (GA4 gtag on web + Firebase Analytics on native).
 */
export async function trackConversionEvent(
  eventName: string,
  params?: GaParams,
): Promise<void> {
  queueOrFireGtag(eventName, params);
  await logFirebaseEvent(eventName, params);
}

/**
 * Account created (email register or OAuth first-time user).
 * Deduped per browser session / device so OAuth + email paths do not double-count.
 */
export async function trackSignupComplete(params?: {
  method?: string;
  source?: string;
}): Promise<void> {
  if (await alreadySent(SIGNUP_DEDUP_KEY)) return;
  await markSent(SIGNUP_DEDUP_KEY);

  const payload: GaParams = {
    method: params?.method || 'unknown',
    ...(params?.source ? { source: params.source } : {}),
  };

  await trackConversionEvent(GA_EVENT_SIGNUP_COMPLETE, payload);
  logMetaCompleteRegistration(params?.method);
}

/**
 * User finished Easy Mode onboarding (activation).
 * Deduped so remount / Strict Mode does not double-fire.
 */
export async function trackActivation(params?: {
  source?: string;
}): Promise<void> {
  if (await alreadySent(ACTIVATION_DEDUP_KEY)) return;
  await markSent(ACTIVATION_DEDUP_KEY);

  await trackConversionEvent(GA_EVENT_ACTIVATION, {
    source: params?.source || 'onboarding_complete',
  });
}
